// Misc
import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { withStyles } from '@material-ui/core/styles'

// Utils
import { updateCustomField } from 'utils/proposals'
import { withTimezone, momentOffset, setTime } from 'utils/time'

// Constants
import { SINGLE_EVENT, SCHEDULED_EVENT, ABSTRACT_SERVICE } from 'constants/proposalTypes'

// Selectors
import { connectGetters } from 'react-redux-getters'
import { getMe } from 'selectors/me'

// Atoms
import Warning from 'atoms/Warning'
import DatePicker from 'atoms/DatePicker'
import TimePicker from 'atoms/TimePicker'
import Spacer from 'atoms/Spacer'
import { StyledLink } from 'atoms/Link'
import ScheduleEditor from 'components/ScheduleEditor'
import Expand from 'atoms/Expand'
import TextInput from 'atoms/TextInput'
import FieldLayout from '../FieldLayout'

// Styles
const styles = {
  inputs: {
    display: 'flex',
  },
  input: {
    width: '50%',
  }
}

const mapGettersToProps = state => ({
  meGetter: getMe(state)
})

export default
@connectGetters(mapGettersToProps)
@withTimezone
@withStyles(styles)
class WhenEventEditor extends Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    proposal: PropTypes.object.isRequired,
    meGetter: PropTypes.object.isRequired,
    onDelete: PropTypes.func.isRequired,
    field: PropTypes.object.isRequired,
    normalizedFields: PropTypes.array.isRequired,
    onProposalUpdate: PropTypes.func.isRequired,
    utcOffset: PropTypes.number,
  }

  handleBeginDateChange = (date) => {
    const { proposal, onProposalUpdate, utcOffset } = this.props
    const { beginsAt, selectOnlyDate, endsAt } = proposal
    const nextBeginsAt = !selectOnlyDate && beginsAt ? setTime(date, beginsAt, utcOffset) : date

    let nextEndsAt
    if (proposal.type === SINGLE_EVENT) {
      nextEndsAt = nextBeginsAt
    } else if (endsAt && !moment(endsAt).isBefore(moment(nextBeginsAt))) {
      nextEndsAt = endsAt
    } else {
      nextEndsAt = null
    }

    onProposalUpdate({
      beginsAt: nextBeginsAt,
      endsAt: nextEndsAt,
    })
  }

  handleBeginTimeChange = (date) => {
    const { proposal, onProposalUpdate, utcOffset } = this.props
    const { beginsAt } = proposal
    let nextBeginsAt
    let selectOnlyDate

    if (!date) {
      selectOnlyDate = true
      nextBeginsAt = beginsAt && setTime(beginsAt, momentOffset(utcOffset).startOf('day'), utcOffset)
    } else {
      selectOnlyDate = false
      nextBeginsAt = beginsAt ? setTime(beginsAt, date, utcOffset) : date
    }

    onProposalUpdate({
      beginsAt: nextBeginsAt,
      endsAt: nextBeginsAt,
      selectOnlyDate,
    })
  }

  handleEndDateChange = (date) => {
    const { onProposalUpdate } = this.props
    onProposalUpdate({ endsAt: date })
  }

  handleExpandToggle = () => {
    const { proposal, onProposalUpdate, utcOffset } = this.props
    let proposalData
    if (proposal.type === SINGLE_EVENT) {
      proposalData = {
        type: SCHEDULED_EVENT,
        selectOnlyDate: false,
        beginsAt: proposal.beginsAt && momentOffset(utcOffset, proposal.beginsAt).startOf('day'),
        endsAt: null,
      }
    } else {
      proposalData = {
        type: SINGLE_EVENT,
        selectOnlyDate: true,
        endsAt: proposal.beginsAt,
      }
    }
    onProposalUpdate(proposalData)
  }

  handleScheduleChange = (schedule) => {
    const { normalizedFields, field, onProposalUpdate } = this.props
    onProposalUpdate({
      data: {
        fields: updateCustomField(normalizedFields, field.key, { schedule })
      }
    })
  }

  handlePlaceholderSave = (placeholder) => {
    const { field, normalizedFields, onProposalUpdate } = this.props
    onProposalUpdate({
      data: {
        fields: updateCustomField(normalizedFields, field.key, { placeholder })
      }
    })
  }

  handleDelete = () => {
    const { onDelete } = this.props
    onDelete({
      type: ABSTRACT_SERVICE,
      selectOnlyDate: false,
      seats: null,
    })
  }

  renderBeginDate() {
    const { proposal, utcOffset } = this.props
    const placeholder = proposal.type === SINGLE_EVENT ? 'Select date' : 'Start date'
    const defaultDate = proposal.beginsAt && new Date(proposal.beginsAt)
    const minDate = new Date()
    return (
      <DatePicker
        required
        minDate={minDate}
        placeholder={placeholder}
        defaultDate={defaultDate}
        utcOffset={utcOffset}
        onChange={this.handleBeginDateChange}
      />
    )
  }

  renderBeginTime() {
    const { proposal, utcOffset } = this.props
    const { beginsAt, selectOnlyDate } = proposal
    const defaultDate = selectOnlyDate ? null : beginsAt && new Date(beginsAt)
    return (
      <TimePicker
        placeholder="Add a time (optional)"
        defaultDate={defaultDate}
        utcOffset={utcOffset}
        onChange={this.handleBeginTimeChange}
      />
    )
  }

  renderEndDate() {
    const { proposal, utcOffset } = this.props
    const date = proposal.endsAt && new Date(proposal.endsAt)
    const minDate = proposal.beginsAt ? new Date(proposal.beginsAt) : new Date()
    return (
      <DatePicker
        minDate={minDate}
        minDateErrorMessage="End date can't be before begin date"
        placeholder="Add an end date (optional)"
        defaultDate={date}
        utcOffset={utcOffset}
        onChange={this.handleEndDateChange}
      />
    )
  }

  renderSchedule() {
    const { meGetter, field } = this.props
    const { business } = meGetter.data
    const { schedule = [] } = field

    if (!business.timezone) {
      return (
        <Warning>
          To enable recurring options you must specify your timezone
          under
          {' '}
          <StyledLink to="/business">business</StyledLink>
          {' '}
          tab
        </Warning>
      )
    }

    return (
      <ScheduleEditor
        schedule={schedule}
        onChange={this.handleScheduleChange}
        addFormTitle="Add event time"
        editFormTitle="Edit event time"
      />
    )
  }

  renderRecurring() {
    const { proposal } = this.props

    if (proposal.hasRequests) {
      if (proposal.type === SINGLE_EVENT) return null

      return (
        <Fragment>
          <Spacer />
          { this.renderSchedule() }
        </Fragment>
      )
    }

    return (
      <Fragment>
        <Spacer />
        <Expand
          expanded={proposal.type === SCHEDULED_EVENT}
          onToggle={this.handleExpandToggle}
          label="Select if recurring"
        >
          { this.renderSchedule() }
        </Expand>
      </Fragment>
    )
  }

  render() {
    const { proposal, classes, field } = this.props
    const onDelete = !proposal.hasRequests ? this.handleDelete : undefined
    return (
      <Fragment>
        <Spacer />
        <FieldLayout title="When" onDelete={onDelete}>
          <div className={classes.inputs}>
            <div className={classes.input}>
              { this.renderBeginDate() }
            </div>
            <div className={classes.input}>
              { proposal.type === SINGLE_EVENT
                ? this.renderBeginTime()
                : this.renderEndDate()
              }
            </div>
          </div>
          { proposal.type === SCHEDULED_EVENT && (
            <Fragment>
              <Spacer />
              <TextInput
                maxLength={100}
                multiline
                label="Instructions"
                placeholder="Enter instructions here (optional)"
                defaultValue={field.placeholder || ''}
                onSave={this.handlePlaceholderSave}
              />
            </Fragment>
          ) }
        </FieldLayout>
        { this.renderRecurring() }
      </Fragment>
    )
  }
}
